import { zodResolver } from '@hookform/resolvers/zod';
import { AlertDialog, DatePicker, FormField, useToast } from '@ntconsult/aluma-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useCloseAllocation } from '../hooks/useCloseAllocation';
import type { Allocation } from '../types/Allocation';
import { formatDisplayDate, parseApiDate, toApiDate } from '../utils/data';
import { i18nKeyForError, parseApiError } from '../utils/errors';
import { professionalLabel } from '../utils/labels';

export type CloseAllocationTarget = {
  allocation: Allocation;
  professional: { registrationNumber: number | null; name: string };
};

type CloseAllocationModalProps = {
  open: boolean;
  onClose: () => void;
  target: CloseAllocationTarget | null;
  today: string;
};

const schema = z.object({ endDate: z.date() });
type FormValues = z.infer<typeof schema>;

function FormBody({
  target,
  today,
  onClose,
}: {
  target: CloseAllocationTarget;
  today: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation(['alocacao', 'common']);
  const { toast } = useToast();
  const closeMutation = useCloseAllocation();
  const { allocation, professional } = target;

  // Premissa P3 (`01-regras-v0.md` §5, `04-spec-api-back.md` §4.3): alocação
  // Futura encerra na própria data de início (mínimo e default); Ativa
  // encerra hoje por default, mínimo é a data de início.
  const minDate = parseApiDate(allocation.startDate) as Date;
  const defaultDate = allocation.status === 'upcoming' ? minDate : (parseApiDate(today) as Date);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { endDate: defaultDate },
  });

  const endDate = watch('endDate');

  const onSubmit = handleSubmit((values) => {
    closeMutation.mutate(
      { id: allocation.id, endDate: toApiDate(values.endDate) },
      {
        onSuccess: () => {
          toast.success({ title: t('alocacao:toasts.encerrarSucesso') });
          onClose();
        },
        onError: (error) => {
          const apiError = parseApiError(error);
          toast.danger({
            title: t((apiError ? i18nKeyForError(apiError.code) : 'alocacao:toasts.erro') as never),
          });
        },
      },
    );
  });

  return (
    <form onSubmit={onSubmit} noValidate>
      <AlertDialog.Title>{t('alocacao:encerrar.titulo')}</AlertDialog.Title>
      <AlertDialog.Description>
        {t('alocacao:encerrar.descricao', {
          profissional: professionalLabel(professional),
          projeto: `${allocation.project.axisCode} - ${allocation.project.name}`,
        })}
      </AlertDialog.Description>

      <div className="flex flex-col gap-4 py-4">
        <FormField
          id="encerrar-end-date"
          label={t('alocacao:encerrar.dtFim')}
          error={errors.endDate ? t('common:errors.required') : undefined}
          required
        >
          <Controller
            control={control}
            name="endDate"
            render={({ field }) => (
              <DatePicker
                id="encerrar-end-date"
                value={field.value}
                onChange={(d) => d && field.onChange(d)}
                minDate={minDate}
              />
            )}
          />
        </FormField>
      </div>

      <AlertDialog.Footer>
        <AlertDialog.Cancel type="button">{t('common:cancel')}</AlertDialog.Cancel>
        <AlertDialog.Action type="submit" loading={closeMutation.isPending}>
          {t('alocacao:encerrar.confirmar', {
            data: formatDisplayDate(toApiDate(endDate), i18n.language),
          })}
        </AlertDialog.Action>
      </AlertDialog.Footer>
    </form>
  );
}

/** Regra 17 (`02-spec-tela-design.md`): um único campo, o botão nomeia a data. */
export function CloseAllocationModal({ open, onClose, target, today }: CloseAllocationModalProps) {
  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && onClose()}>
      <AlertDialog.Content>
        {target && (
          <FormBody key={target.allocation.id} target={target} today={today} onClose={onClose} />
        )}
      </AlertDialog.Content>
    </AlertDialog>
  );
}
